import {
  cloudBoardStateSchema,
  cloudActiveCapturesSchema,
  cloudCaptureResponseSchema,
  cloudCaptureStatusSchema,
  cloudConnectionResponseSchema,
  cloudRestoreHistorySchema,
  cloudRestoreReportSchema,
  cloudSnapshotHistorySchema,
  cloudSnapshotDeleteResponseSchema,
  cloudSnapshotWarningsSchema,
  activeInPlaceRestorePreparationResponseSchema,
  inPlaceRestoreConfirmationResponseSchema,
  inPlaceRestoreControlResponseSchema,
  inPlaceRestoreHistorySchema,
  inPlaceRestorePreviewSchema,
  inPlaceRestorePreparationResponseSchema,
  inPlaceRestoreReportSchema,
  jobControlResponseSchema,
  snapshotComparisonSchema,
  portalHandoffResponseSchema,
  restorePreviewSchema,
  restorePreparationResponseSchema,
  selectiveRestorePreviewSchema,
  selectiveRestoreCandidatesSchema,
  type CloudBoardState,
  type CloudActiveCaptures,
  type CloudCaptureResponse,
  type CloudCaptureStatus,
  type CloudConnectionResponse,
  type CloudRestoreHistory,
  type CloudRestoreReport,
  type CloudSnapshotHistory,
  type CloudSnapshotDeleteResponse,
  type CloudSnapshotWarnings,
  type ActiveInPlaceRestorePreparationResponse,
  type CloudSettingsUpdateRequest,
  type JobControlAction,
  type JobControlResponse,
  type InPlaceRestoreConfirmationRequest,
  type InPlaceRestoreConfirmationResponse,
  type InPlaceRestoreControlResponse,
  type InPlaceRestoreHistory,
  type InPlaceRestorePreview,
  type InPlaceRestorePreparationResponse,
  type InPlaceRestoreReport,
  type PortalHandoffResponse,
  type RestorePreview,
  type RestorePreparationResponse,
  type SelectiveRestorePreview,
  type SelectiveRestoreCandidates,
  type SelectiveRestoreScope,
  type SnapshotComparison,
} from "@board-preserve/contracts";

import type {
  TrelloCloudConnectionSession,
  TrelloCloudContextSession,
} from "./trello-authorization";

export interface CloudDisconnectResponse {
  disconnected: true;
  disconnectedAt: string;
  retainedBackups: "until_expiry";
  workspaceId: string;
}

export class CloudApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "CloudApiError";
  }
}

function parseCloudDisconnectResponse(value: unknown): CloudDisconnectResponse {
  if (
    typeof value !== "object" ||
    value === null ||
    !("disconnected" in value) ||
    value.disconnected !== true ||
    !("disconnectedAt" in value) ||
    typeof value.disconnectedAt !== "string" ||
    !("retainedBackups" in value) ||
    value.retainedBackups !== "until_expiry" ||
    !("workspaceId" in value) ||
    typeof value.workspaceId !== "string"
  ) {
    throw new Error("Cloud Preserve returned an invalid disconnect response.");
  }
  return value as CloudDisconnectResponse;
}

export function readCloudApiUrl(value: string | undefined): string | null {
  const normalized = value?.trim();

  if (!normalized) {
    return null;
  }

  const url = new URL(normalized);
  const localDevelopment =
    url.protocol === "http:" &&
    (url.hostname === "localhost" || url.hostname === "127.0.0.1");

  if (url.protocol !== "https:" && !localDevelopment) {
    throw new Error("Cloud Preserve API must use HTTPS.");
  }

  return url.toString().replace(/\/$/, "");
}

export async function connectCloudPreserve(
  apiUrl: string | null,
  session: TrelloCloudConnectionSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudConnectionResponse> {
  if (!apiUrl) {
    throw new Error("Cloud Preserve API is not configured for this build.");
  }

  const response = await fetchImplementation(`${apiUrl}/v1/cloud/connections`, {
    body: JSON.stringify({ trelloToken: session.trelloToken }),
    credentials: "omit",
    headers: {
      authorization: `Bearer ${session.contextJwt}`,
      "content-type": "application/json",
    },
    method: "POST",
    referrerPolicy: "no-referrer",
  });
  const payload = (await response.json()) as unknown;

  if (!response.ok) {
    const detail =
      typeof payload === "object" &&
      payload !== null &&
      "detail" in payload &&
      typeof payload.detail === "string"
        ? payload.detail
        : "Cloud Preserve could not connect this board.";
    throw new CloudApiError(response.status, detail);
  }

  return cloudConnectionResponseSchema.parse(payload);
}

export async function disconnectCloudPreserve(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudDisconnectResponse> {
  if (!apiUrl) {
    throw new Error("Cloud Preserve API is not configured for this build.");
  }

  const response = await fetchImplementation(
    `${apiUrl}/v1/cloud/connections/current`,
    {
      credentials: "omit",
      headers: { authorization: `Bearer ${session.contextJwt}` },
      method: "DELETE",
      referrerPolicy: "no-referrer",
    },
  );
  const payload = (await response.json()) as unknown;

  if (!response.ok) {
    const detail =
      typeof payload === "object" &&
      payload !== null &&
      "detail" in payload &&
      typeof payload.detail === "string"
        ? payload.detail
        : "Cloud Preserve could not disconnect this Workspace.";
    throw new Error(detail);
  }

  return parseCloudDisconnectResponse(payload);
}

async function cloudSettingsRequest(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  init: RequestInit,
  fetchImplementation: typeof fetch,
): Promise<CloudBoardState> {
  if (!apiUrl) {
    throw new Error("Cloud Preserve API is not configured for this build.");
  }

  const response = await fetchImplementation(
    `${apiUrl}/v1/cloud/boards/current/settings`,
    {
      ...init,
      credentials: "omit",
      headers: {
        ...init.headers,
        authorization: `Bearer ${session.contextJwt}`,
      },
      referrerPolicy: "no-referrer",
    },
  );
  const payload = (await response.json()) as unknown;

  if (!response.ok) {
    const detail =
      typeof payload === "object" &&
      payload !== null &&
      "detail" in payload &&
      typeof payload.detail === "string"
        ? payload.detail
        : "Cloud Preserve settings could not be updated.";
    throw new CloudApiError(response.status, detail);
  }

  return cloudBoardStateSchema.parse(payload);
}

export function updateCloudSettings(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  settings: CloudSettingsUpdateRequest,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudBoardState> {
  return cloudSettingsRequest(
    apiUrl,
    session,
    {
      body: JSON.stringify(settings),
      headers: { "content-type": "application/json" },
      method: "PATCH",
    },
    fetchImplementation,
  );
}

export function readCloudSettings(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudBoardState> {
  return cloudSettingsRequest(
    apiUrl,
    session,
    { method: "GET" },
    fetchImplementation,
  );
}

async function signedCloudRequest(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  path: string,
  init: RequestInit,
  fetchImplementation: typeof fetch,
): Promise<unknown> {
  if (!apiUrl) {
    throw new Error("Cloud Preserve API is not configured for this build.");
  }
  const response = await fetchImplementation(`${apiUrl}${path}`, {
    ...init,
    credentials: "omit",
    headers: {
      ...init.headers,
      authorization: `Bearer ${session.contextJwt}`,
    },
    referrerPolicy: "no-referrer",
  });
  const payload = (await response.json()) as unknown;
  if (!response.ok) {
    const detail =
      typeof payload === "object" &&
      payload !== null &&
      "detail" in payload &&
      typeof payload.detail === "string"
        ? payload.detail
        : "Cloud Preserve could not complete this request.";
    throw new Error(detail);
  }
  return payload;
}

export async function startCloudCapture(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  idempotencyKey: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudCaptureResponse> {
  return cloudCaptureResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots",
      {
        body: JSON.stringify({ idempotencyKey }),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function readCloudCaptureStatus(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudCaptureStatus> {
  return cloudCaptureStatusSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/snapshots/${encodeURIComponent(snapshotId)}/status`,
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function readActiveCloudCaptures(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudActiveCaptures> {
  return cloudActiveCapturesSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/active",
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function controlCloudJob(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  jobId: string,
  action: JobControlAction["action"],
  fetchImplementation: typeof fetch = fetch,
): Promise<JobControlResponse> {
  return jobControlResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/jobs/${encodeURIComponent(jobId)}/control`,
      {
        body: JSON.stringify({ action }),
        headers: { "content-type": "application/json" },
        method: "PATCH",
      },
      fetchImplementation,
    ),
  );
}

export async function readCloudHistory(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudSnapshotHistory> {
  return cloudSnapshotHistorySchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots",
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function deleteCloudSnapshot(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  confirmation: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudSnapshotDeleteResponse> {
  return cloudSnapshotDeleteResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/snapshots/${encodeURIComponent(snapshotId)}`,
      {
        body: JSON.stringify({ confirmation }),
        headers: { "content-type": "application/json" },
        method: "DELETE",
      },
      fetchImplementation,
    ),
  );
}

export async function readCloudSnapshotWarnings(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudSnapshotWarnings> {
  return cloudSnapshotWarningsSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/snapshots/${encodeURIComponent(snapshotId)}/warnings`,
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function downloadCloudSnapshot(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  timezoneOffsetMinutesOrFetch: number | typeof fetch = 0,
  suppliedFetchImplementation: typeof fetch = fetch,
): Promise<Response> {
  if (!apiUrl) {
    throw new Error("Cloud Preserve API is not configured for this build.");
  }
  const timezoneOffsetMinutes =
    typeof timezoneOffsetMinutesOrFetch === "number"
      ? timezoneOffsetMinutesOrFetch
      : 0;
  const fetchImplementation =
    typeof timezoneOffsetMinutesOrFetch === "function"
      ? timezoneOffsetMinutesOrFetch
      : suppliedFetchImplementation;
  const response = await fetchImplementation(
    `${apiUrl}/v1/cloud/boards/current/snapshots/${encodeURIComponent(snapshotId)}/download?timezoneOffsetMinutes=${encodeURIComponent(String(timezoneOffsetMinutes))}`,
    {
      credentials: "omit",
      headers: { authorization: `Bearer ${session.contextJwt}` },
      method: "GET",
      referrerPolicy: "no-referrer",
    },
  );
  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    let detail = "The preserved backup could not be downloaded.";
    if (contentType.includes("application/problem+json")) {
      try {
        const payload = (await response.clone().json()) as unknown;
        if (
          typeof payload === "object" &&
          payload !== null &&
          "detail" in payload &&
          typeof payload.detail === "string"
        ) {
          detail = payload.detail;
        }
      } catch {
        // Keep the stable content-free fallback for malformed error responses.
      }
    }
    await response.body?.cancel();
    throw new Error(detail);
  }
  return response;
}

export function startNativeCloudSnapshotDownload(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  timezoneOffsetMinutes = 0,
): void {
  if (!apiUrl) {
    throw new Error("Cloud Preserve API is not configured for this build.");
  }
  if (!session.contextJwt || session.contextJwt.length > 12_000) {
    throw new Error("The signed Trello download context is invalid.");
  }

  const targetName = `board-preserve-download-${crypto.randomUUID()}`;
  const frame = document.createElement("iframe");
  frame.hidden = true;
  frame.name = targetName;
  frame.setAttribute("aria-hidden", "true");
  frame.setAttribute("tabindex", "-1");

  const form = document.createElement("form");
  form.action = `${apiUrl}/v1/cloud/boards/current/snapshots/${encodeURIComponent(snapshotId)}/native-download?timezoneOffsetMinutes=${encodeURIComponent(String(timezoneOffsetMinutes))}`;
  form.enctype = "application/x-www-form-urlencoded";
  form.hidden = true;
  form.method = "POST";
  form.target = targetName;

  const context = document.createElement("input");
  context.autocomplete = "off";
  context.name = "context";
  context.type = "hidden";
  context.value = session.contextJwt;
  form.append(context);
  document.body.append(frame, form);
  form.submit();
  // Remove the signed context from the DOM immediately. The empty target must
  // stay alive long enough for the browser-owned attachment response to begin.
  form.remove();
  window.setTimeout(() => frame.remove(), 60_000);
}

export async function compareCloudSnapshots(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fromSnapshotId: string,
  toSnapshotId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<SnapshotComparison> {
  const query = new URLSearchParams({
    from: fromSnapshotId,
    to: toSnapshotId,
  });
  return snapshotComparisonSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/snapshots/compare?${query.toString()}`,
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function previewCloudRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<RestorePreview> {
  return restorePreviewSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/restore-preview",
      {
        body: JSON.stringify({ snapshotId }),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function previewCloudInPlaceRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<InPlaceRestorePreview> {
  return inPlaceRestorePreviewSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/in-place-restore-preview",
      {
        body: JSON.stringify({ targetSnapshotId: snapshotId }),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function prepareCloudInPlaceRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  input: {
    createFreshSafetyBackup: boolean;
    idempotencyKey: string;
    previewSha256: string;
    targetSnapshotId: string;
  },
  fetchImplementation: typeof fetch = fetch,
): Promise<InPlaceRestorePreparationResponse> {
  return inPlaceRestorePreparationResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/in-place-restore-preparations",
      {
        body: JSON.stringify(input),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function readActiveCloudInPlacePreparation(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<ActiveInPlaceRestorePreparationResponse> {
  return activeInPlaceRestorePreparationResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/in-place-restore-preparations",
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function readCloudInPlacePreparation(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  preparationId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<InPlaceRestorePreparationResponse> {
  return inPlaceRestorePreparationResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/snapshots/in-place-restore-preparations/${encodeURIComponent(preparationId)}`,
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function confirmCloudInPlaceRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  input: InPlaceRestoreConfirmationRequest,
  fetchImplementation: typeof fetch = fetch,
): Promise<InPlaceRestoreConfirmationResponse> {
  return inPlaceRestoreConfirmationResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/in-place-restore-confirmations",
      {
        body: JSON.stringify(input),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function readCloudInPlaceRestoreHistory(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<InPlaceRestoreHistory> {
  return inPlaceRestoreHistorySchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/in-place-restores",
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function readCloudInPlaceRestoreReport(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  jobId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<InPlaceRestoreReport> {
  return inPlaceRestoreReportSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/in-place-restores/${encodeURIComponent(jobId)}`,
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function controlCloudInPlaceRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  jobId: string,
  action: "cancel" | "pause" | "resume" | "retry",
  fetchImplementation: typeof fetch = fetch,
): Promise<InPlaceRestoreControlResponse> {
  return inPlaceRestoreControlResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/in-place-restores/${encodeURIComponent(jobId)}/control`,
      {
        body: JSON.stringify({ action }),
        headers: { "content-type": "application/json" },
        method: "PATCH",
      },
      fetchImplementation,
    ),
  );
}

export async function confirmCloudRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  preview: RestorePreview,
  idempotencyKey: string,
  fetchImplementation: typeof fetch = fetch,
  allowDuplicate = false,
): Promise<RestorePreparationResponse> {
  return restorePreparationResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/restore-confirmations",
      {
        body: JSON.stringify({
          acknowledgements: {
            createsNewPrivateBoard: true,
            ...(allowDuplicate ? { duplicateRestoreReviewed: true } : {}),
            fidelityReviewed: true,
            noOverwriteOrDelete: true,
            sourceUnchanged: true,
          },
          duplicatePolicy: allowDuplicate ? "allow_explicit" : "block",
          idempotencyKey,
          planSha256: preview.planSha256,
          snapshotId: preview.snapshot.snapshotId,
        }),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function previewCloudSelectiveRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  scope: SelectiveRestoreScope,
  selectedSourceId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<SelectiveRestorePreview> {
  return selectiveRestorePreviewSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/selective-restore-preview",
      {
        body: JSON.stringify({ scope, selectedSourceId, snapshotId }),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function readCloudSelectiveRestoreCandidates(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  snapshotId: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<SelectiveRestoreCandidates> {
  return selectiveRestoreCandidatesSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/snapshots/${encodeURIComponent(snapshotId)}/selective-restore-candidates`,
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function confirmCloudSelectiveRestore(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  preview: SelectiveRestorePreview,
  idempotencyKey: string,
  fetchImplementation: typeof fetch = fetch,
): Promise<RestorePreparationResponse> {
  return restorePreparationResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/snapshots/selective-restore-confirmations",
      {
        body: JSON.stringify({
          acknowledgements: {
            fidelityReviewed: true,
            noOverwriteOrDelete: true,
            restoreIntoOriginalBoard: true,
          },
          duplicatePolicy: "block",
          idempotencyKey,
          planSha256: preview.planSha256,
          scope: preview.selection.scope,
          selectedSourceId: preview.selection.sourceId,
          snapshotId: preview.snapshot.snapshotId,
        }),
        headers: { "content-type": "application/json" },
        method: "POST",
      },
      fetchImplementation,
    ),
  );
}

export async function readCloudRestoreHistory(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudRestoreHistory> {
  return cloudRestoreHistorySchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/boards/current/restores",
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function readCloudRestoreReport(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  jobId: string,
  after = 0,
  limit = 100,
  fetchImplementation: typeof fetch = fetch,
): Promise<CloudRestoreReport> {
  const query = new URLSearchParams({
    after: String(after),
    limit: String(limit),
  });
  return cloudRestoreReportSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      `/v1/cloud/boards/current/restores/${encodeURIComponent(jobId)}?${query.toString()}`,
      { method: "GET" },
      fetchImplementation,
    ),
  );
}

export async function createPortalHandoff(
  apiUrl: string | null,
  session: TrelloCloudContextSession,
  fetchImplementation: typeof fetch = fetch,
): Promise<PortalHandoffResponse> {
  return portalHandoffResponseSchema.parse(
    await signedCloudRequest(
      apiUrl,
      session,
      "/v1/cloud/portal/handoffs",
      { method: "POST" },
      fetchImplementation,
    ),
  );
}
